import { createAsyncThunk, createSlice, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import axios from 'axios';
import API, { typeThrownException } from '@acorn-portal/shared/src/services/AxiosInstance';
import { TelematicsMileageClient, PolicyExistsDto } from '@acorn-portal/shared/src/services/telematicsMileageApi';

interface TelematicsEnabledPolicyState {
	policyExists: PolicyExistsDto | null;
	loading: boolean;
	error: string | null;
}

const initialState: TelematicsEnabledPolicyState = {
	policyExists: null,
	loading: false,
	error: null,
};

interface CheckPolicyExistsParams {
	policyReference: string;
}

export const checkPolicyExists = createAsyncThunk<PolicyExistsDto, CheckPolicyExistsParams, { rejectValue: unknown }>(
	'telematicsEnabledPolicy/checkPolicyExists',
	async (params: CheckPolicyExistsParams, { rejectWithValue }) => {
		try {
			const client = new TelematicsMileageClient(undefined, API);
			const response = await client.exists(params.policyReference);
			return response;
		} catch (e) {
			if (axios.isAxiosError(e) && e.response) {
				if (e.response.status < 200 || e.response.status > 300) {
					return rejectWithValue(e);
				}
			}
			if (typeThrownException(e) && e.status) {
				if (e.status < 200 || e.status > 300) {
					return rejectWithValue(e);
				}
			}
			throw e;
		}
	},
);

export const telematicsEnabledPolicySlice = createSlice({
	name: 'telematicsEnabledPolicy',
	initialState,
	reducers: {
		setPolicyExists: (state: TelematicsEnabledPolicyState, action: PayloadAction<PolicyExistsDto>) => {
			state.policyExists = action.payload;
		},
		clearPolicyExists: (state: TelematicsEnabledPolicyState) => {
			state.policyExists = null;
			state.loading = false;
			state.error = null;
		},
		clearAll: () => initialState,
	},
	extraReducers: (builder: ActionReducerMapBuilder<TelematicsEnabledPolicyState>) => {
		builder.addCase(checkPolicyExists.pending, (state: TelematicsEnabledPolicyState) => {
			state.loading = true;
			state.error = null;
		});
		builder.addCase(checkPolicyExists.fulfilled, (state: TelematicsEnabledPolicyState, { payload }) => {
			state.loading = false;
			state.policyExists = payload;
		});
		builder.addCase(checkPolicyExists.rejected, (state: TelematicsEnabledPolicyState, action) => {
			state.loading = false;
			state.error = action.error.message ?? 'Failed to check if policy exists';
		});
	},
});

export const { setPolicyExists, clearPolicyExists, clearAll } = telematicsEnabledPolicySlice.actions;
export default telematicsEnabledPolicySlice.reducer;
